import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'
import { useCallback, useMemo, useRef } from 'react'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommonTrackingProperties } from './useCommonTrackingProperties'

export type CtaProperties = {
  product?: string
  clientName?: string
  upgradePathName?: string
  extraProperties?: Record<string, unknown>
}

type CtaReturnValue = {
  clicked: () => void
  viewed: () => void
}

export const useCta = (cta: CTA, options?: CtaProperties): CtaReturnValue => {
  const {
    product = Product.Publish,
    clientName = Client.PublishWeb,
    upgradePathName = null,
    extraProperties = {},
  } = options ?? {}

  const organizationId = useOrganizationId()
  const commonTrackingProperties = useCommonTrackingProperties()
  const hasFiredViewed = useRef(false)

  const clicked = useCallback((): void => {
    if (!organizationId || !commonTrackingProperties) return
    BufferTracker.cTAClicked({
      clientName,
      product,
      cta,
      upgradePathName,
      ...commonTrackingProperties,
      ...extraProperties,
    })
  }, [
    organizationId,
    clientName,
    product,
    cta,
    upgradePathName,
    commonTrackingProperties,
    extraProperties,
  ])

  const viewed = useCallback((): void => {
    if (!organizationId || !upgradePathName || !commonTrackingProperties) return
    if (hasFiredViewed.current) return
    BufferTracker.upgradePathViewed({
      clientName,
      product,
      cta,
      upgradePathName,
      ...commonTrackingProperties,
      ...extraProperties,
    })
    hasFiredViewed.current = true
  }, [
    organizationId,
    clientName,
    product,
    cta,
    upgradePathName,
    commonTrackingProperties,
    extraProperties,
  ])

  return useMemo(() => ({ clicked, viewed }), [clicked, viewed])
}
